import { observer } from 'mobx-react-lite'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useAdapter } from '@/components/Adapter'
import { useRouter } from 'next/router'
import { Tabs, Skeleton, Modal } from '@gate/gui'
import { Flex } from '@mantine/core'
import { getArticleMulti } from 'api/price'
import { useRequest, useUpdateEffect } from 'ahooks'
import BaseSuspense from '@/components/BaseSuspense'
import { eventTracker } from '@/utils/collectEvent'
import MoreTip from './MoreTip'
import { PriceBannerStyled, PriceBannerSkeletonStyled, PriceTitleStyled, PriceTabsStyled } from './style'
import { useStore } from '@/store/index'
import PriceBannerItem from './Item/Item'
import('@/components/WebComponent')

export enum EnumPriceTopTitleTab {
  /** 今日行情分析 */
  MarketToday = 'MarketToday',
  /** 一周行业盘点 */
  WeeklyRecap = 'WeeklyRecap',
}

const PriceBanner = () => {
  const { locale } = useRouter()
  const { t } = useTranslation()
  const { isH5 } = useAdapter()

  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [tabs, setTabs] = useState<EnumPriceTopTitleTab>(EnumPriceTopTitleTab.MarketToday)

  const { dark } = useStore().globalStore
  const { marketToday, setMarketToday } = useStore().baseStore
  // 一周行业盘点 | loading
  const [weeklyRecapLoading, setWeeklyRecapLoading] = useState<boolean>(true)

  const timerRef = useRef<any>(null)
  const modalDataRef = useRef<HTMLDivElement>(null)

  //  一周行业盘点 、今日行情分析
  const { data: weeklyRecapData, run } = useRequest(getArticleMulti, {
    manual: false,
    defaultParams: [{ data_tabs: '1083001,1083002', lang: locale ?? 'en', is_night: dark === '2' ? 1 : 0 }],
    onFinally() {
      setWeeklyRecapLoading(false)
    },
    onSuccess(res) {
      setMarketToday(res)
    },
  })
  useUpdateEffect(() => {
    run({
      data_tabs: '1083001,1083002',
      lang: locale ?? 'en',
      is_night: dark === '2' ? 1 : 0,
    })
  }, [locale, dark])

  const onTabChange = (tab: EnumPriceTopTitleTab) => {
    if (tab !== tabs) {
      setTabs(tab)
      eventTracker('price_overview_click', {
        tab_name: tab === EnumPriceTopTitleTab.MarketToday ? 'market_today' : 'weekly_recap',
      })
    }
  }

  /**
   * @description: 趋势&表现图 | 骨架屏
   * @param {*} React
   * @return {*}
   */
  const PriceBannerSkeleton = (): React.ReactElement => {
    return (
      <PriceBannerSkeletonStyled>
        <Skeleton w={'100%'} h={120} />
      </PriceBannerSkeletonStyled>
    )
  }

  useUpdateEffect(() => {
    timerRef.current = setTimeout(() => {
      if (modalDataRef.current && modalVisible) {
        if (tabs === EnumPriceTopTitleTab.MarketToday && weeklyRecapData?.length) {
          modalDataRef.current?.setAttribute('data', JSON.stringify({ value: weeklyRecapData?.[0]?.content ?? '' }))
        }
        if (tabs === EnumPriceTopTitleTab.WeeklyRecap && weeklyRecapData?.length) {
          modalDataRef.current.setAttribute('data', JSON.stringify({ value: weeklyRecapData?.[1]?.content ?? '' }))
        }
      }
    }, 10)
    return () => {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [modalVisible, tabs, weeklyRecapData])

  const dayIntroduction = useMemo(() => {
    if (weeklyRecapData?.length) {
      return weeklyRecapData?.[0]?.introduction || ''
    }
    return marketToday?.[0]?.introduction || ''
  }, [marketToday, weeklyRecapData])

  return (
    <PriceBannerStyled>
      <PriceTitleStyled>{t('P.Market')}</PriceTitleStyled>

      <PriceTabsStyled>
        <Tabs type="primary" value={tabs} onTabChange={onTabChange} w={'100%'}>
          <Flex justify="space-between" align="center">
            <Tabs.List>
              <Tabs.Tab value={EnumPriceTopTitleTab.MarketToday}>
                <h2 className="font-700 size-20-16">{t('P.MarketToday')}</h2>
              </Tabs.Tab>
              <Tabs.Tab value={EnumPriceTopTitleTab.WeeklyRecap}>
                <h2 className="font-700 size-20-16">{t('P.WeeklyRecap')}</h2>
              </Tabs.Tab>
            </Tabs.List>
            {!isH5 && <MoreTip tabs={tabs} handleModalVisible={() => setModalVisible(true)} />}
          </Flex>

          <Tabs.Panel value={EnumPriceTopTitleTab.MarketToday} h={120}>
            <PriceBannerItem onClick={() => setModalVisible(true)} contentHTML={dayIntroduction} />
          </Tabs.Panel>

          <Tabs.Panel value={EnumPriceTopTitleTab.WeeklyRecap} h={120}>
            <BaseSuspense loading={weeklyRecapLoading} fallback={<PriceBannerSkeleton />}>
              <PriceBannerItem
                onClick={() => setModalVisible(true)}
                isClient
                contentHTML={
                  weeklyRecapData?.length && weeklyRecapData?.[1] ? weeklyRecapData?.[1]?.introduction || '' : ''
                }
              />
            </BaseSuspense>
          </Tabs.Panel>

          {/* more样式更改 */}
          {isH5 && <MoreTip tabs={tabs} handleModalVisible={() => setModalVisible(true)} />}
        </Tabs>
      </PriceTabsStyled>

      <Modal
        styles={{
          content: {
            padding: '16px 32px 24px',
          },
          body: {
            overflow: 'hidden',
            borderRadius: '24px',
          },
        }}
        style={{ overflow: 'hidden' }}
        title={tabs === EnumPriceTopTitleTab.MarketToday ? t('P.MarketToday') : t('P.WeeklyRecap')}
        type="md"
        opened={modalVisible}
        onClose={() => setModalVisible(false)}
        withinPortal
      >
        <BaseSuspense loading={weeklyRecapLoading} fallback={<PriceBannerSkeleton />}>
          <web-component-react ref={modalDataRef} />
        </BaseSuspense>
      </Modal>
    </PriceBannerStyled>
  )
}

export default observer(PriceBanner)
